import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../store/auth-context";
import { useNotification } from "../../store/notification-context";

const OTP_LENGTH = 6;
const DEFAULT_TTL_SECONDS = 5 * 60;

const AdminAccessModal = () => {
    const navigate = useNavigate();
    const { isAuthenticated, isProfileComplete, user, verifyAdminCode, requestAdminCode } = useAuth();
    const { pushNotification } = useNotification();
    const [isOpen, setIsOpen] = useState(false);
    const [codeDigits, setCodeDigits] = useState(() => Array(OTP_LENGTH).fill(""));
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRequestingCode, setIsRequestingCode] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [autoRequestCompletedForUserId, setAutoRequestCompletedForUserId] = useState(null);
    const [dismissedForUserId, setDismissedForUserId] = useState(null);
    const code = useMemo(() => codeDigits.join(""), [codeDigits]);

    const shouldRequestAdminVerification = useMemo(() => (
        Boolean(isAuthenticated && isProfileComplete && user?.role === "admin" && user?.adminSessionActive !== true)
    ), [isAuthenticated, isProfileComplete, user]);
    const hasAdminVerificationChannel = Boolean(user?.adminVerificationChannelAvailable);

    useEffect(() => {
        setCodeDigits(Array(OTP_LENGTH).fill(""));
        setErrorMessage("");
        setIsOpen(shouldRequestAdminVerification && dismissedForUserId !== user?.id);
        if (user?.id !== autoRequestCompletedForUserId) {
            setAutoRequestCompletedForUserId(null);
        }
        if (user?.id !== dismissedForUserId) {
            setDismissedForUserId(null);
        }
    }, [shouldRequestAdminVerification, user?.id, autoRequestCompletedForUserId, dismissedForUserId]);

    useEffect(() => {
        if (!user?.adminCodeExpiresAt) {
            if (isOpen) {
                setRemainingSeconds(DEFAULT_TTL_SECONDS);
            } else {
                setRemainingSeconds(0);
            }
            return;
        }

        const updateRemainingSeconds = () => {
            const expiresAtMs = Number(user.adminCodeExpiresAtMs || 0) || new Date(user.adminCodeExpiresAt).getTime();
            const nextRemainingSeconds = Math.max(
                0,
                Math.ceil((expiresAtMs - Date.now()) / 1000)
            );
            setRemainingSeconds(nextRemainingSeconds);
        };

        updateRemainingSeconds();
        const intervalId = window.setInterval(updateRemainingSeconds, 1000);

        return () => window.clearInterval(intervalId);
    }, [isOpen, user?.adminCodeExpiresAt, user?.adminCodeExpiresAtMs]);

    const countdownLabel = useMemo(() => {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }, [remainingSeconds]);

    const hasActiveCode = Boolean(user?.adminCodeExpiresAt && remainingSeconds > 0);
    const isBusy = isSubmitting || isRequestingCode;

    const requestCode = async ({ showSuccessNotification = true } = {}) => {
        setIsRequestingCode(true);
        setErrorMessage("");

        const result = await requestAdminCode();
        setIsRequestingCode(false);

        if (result?.state === "success") {
            setAutoRequestCompletedForUserId(user?.id || null);

            if (showSuccessNotification) {
                pushNotification({
                    title: "Codice admin inviato",
                    message: "Controlla WhatsApp e inserisci il codice entro 5 minuti."
                });
            }
            return result;
        }

        setErrorMessage(result?.message || "Impossibile inviare il codice admin.");
        return result;
    };

    useEffect(() => {
        if (!isOpen || !shouldRequestAdminVerification || !hasAdminVerificationChannel || hasActiveCode || isRequestingCode) {
            return;
        }

        if (!user?.id || autoRequestCompletedForUserId === user.id) {
            return;
        }

        requestCode({ showSuccessNotification: false });
    }, [
        autoRequestCompletedForUserId,
        hasActiveCode,
        isOpen,
        isRequestingCode,
        shouldRequestAdminVerification,
        hasAdminVerificationChannel,
        user?.id
    ]);

    const handleClose = () => {
        setDismissedForUserId(user?.id || null);
        setIsOpen(false);
    };

    const handleResendCode = async () => {
        await requestCode({ showSuccessNotification: true });
    };

    const handleGoToProfile = () => {
        handleClose();
        navigate('/scheda-psicologo');
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage("");

        const result = await verifyAdminCode(code);
        setIsSubmitting(false);

        if (result?.state === "success") {
            pushNotification({
                title: "Accesso admin attivo",
                message: "La sessione admin e stata verificata.",
                actionLabel: "Apri dashboard",
                actionTo: "/admin-dashboard"
            });
            setIsOpen(false);
            setCodeDigits(Array(OTP_LENGTH).fill(""));
            return;
        }

        setErrorMessage(result?.message || "Codice non valido o scaduto.");
    };

    const handleCodeChange = (index, rawValue) => {
        const digit = rawValue.replace(/\D/g, "").slice(-1);
        const nextDigits = [...codeDigits];
        nextDigits[index] = digit;
        setCodeDigits(nextDigits);

        if (digit) {
            const nextInput = document.getElementById(`admin-code-digit-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const handleCodeKeyDown = (index, event) => {
        if (event.key === "Backspace" && !codeDigits[index] && index > 0) {
            const previousInput = document.getElementById(`admin-code-digit-${index - 1}`);
            if (previousInput) {
                previousInput.focus();
            }
        }
    };

    const handleCodePaste = (event) => {
        event.preventDefault();
        const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pastedDigits) {
            return;
        }

        const nextDigits = Array(OTP_LENGTH).fill("");
        pastedDigits.split("").forEach((digit, index) => {
            nextDigits[index] = digit;
        });
        setCodeDigits(nextDigits);
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Verifica accesso Admin</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        {!hasAdminVerificationChannel
                            ? "Per attivare la sessione admin devi prima aggiungere un numero di telefono al tuo profilo."
                            : hasActiveCode
                            ? "Abbiamo inviato un codice WhatsApp al numero associato al tuo account admin."
                            : "Stiamo preparando o richiedendo un codice WhatsApp per verificare la sessione admin."}
                    </Typography>
                    <Typography variant="body2" color={!hasAdminVerificationChannel ? "warning.main" : remainingSeconds > 0 ? "text.secondary" : "error"}>
                        Codice valido per 5 minuti. Tempo residuo: {countdownLabel}
                    </Typography>
                    {!hasAdminVerificationChannel ? (
                        <Alert
                            severity="warning"
                            action={(
                                <Button color="inherit" size="small" onClick={handleGoToProfile}>
                                    Apri scheda
                                </Button>
                            )}
                        >
                            Aggiungi prefisso e numero in "Scheda Psicologo", poi richiedi di nuovo il codice admin.
                        </Alert>
                    ) : null}
                    <Stack direction="row" spacing={1} justifyContent="center" onPaste={handleCodePaste}>
                        {codeDigits.map((digit, index) => (
                            <TextField
                                key={`admin-code-digit-${index}`}
                                id={`admin-code-digit-${index}`}
                                value={digit}
                                onChange={(event) => handleCodeChange(index, event.target.value)}
                                onKeyDown={(event) => handleCodeKeyDown(index, event)}
                                inputProps={{
                                    inputMode: "numeric",
                                    maxLength: 1,
                                    style: {
                                        textAlign: "center",
                                        fontSize: 28,
                                        fontWeight: 600,
                                        padding: "14px 0"
                                    }
                                }}
                                autoFocus={index === 0}
                                sx={{
                                    width: 52,
                                    "& .MuiInputBase-root": {
                                        borderRadius: 1.5
                                    }
                                }}
                                disabled={!hasAdminVerificationChannel}
                            />
                        ))}
                    </Stack>
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                    <Box>
                        <Button onClick={handleResendCode} disabled={isBusy || !hasAdminVerificationChannel}>
                            Reinvia codice
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isBusy}>
                    Chiudi
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isBusy || !hasAdminVerificationChannel || code.length !== 6 || remainingSeconds === 0}>
                    Conferma
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminAccessModal;
